import { Box, Flex, Grid, Space, Text } from '@mantine/core'
import { RetrieveProductTemplateAlgolia } from '@ortros/stoux-lib'
import _ from 'lodash'
import { AnimatePresence, motion } from 'motion/react'

import { pluralWord } from '@/utils/plural-word'
import { ProductTemplate } from 'stoux-shared-types/models/product-template'
import SearchHit from '../SearchHit/SearchHit'

export const RecursiveList = ({
    tree,
    currentLevel = 0,
}: {
    tree: TreeChild
    currentLevel?: number
}) => {
    return (
        <AnimatePresence mode="popLayout">
            {Object.keys(tree).map((key) => {
                const child = tree[key as keyof typeof tree]
                return (
                    <motion.div
                        key={key}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="w-full"
                    >
                        {Array.isArray(child) && (
                            <div className="grid w-full">
                                {!!child.length &&
                                    child.map(
                                        (
                                            product: Partial<ProductTemplate> & {
                                                _highlightResult?: {
                                                    title: {
                                                        value: string
                                                    }
                                                }
                                            },
                                        ) => {
                                            return (
                                                <SearchHit
                                                    key={product.id}
                                                    {...product}
                                                />
                                            )
                                        },
                                    )}
                            </div>
                        )}
                        {!Array.isArray(child) && (
                            <RecursiveList
                                tree={child as TreeChild}
                                currentLevel={currentLevel + 1}
                            />
                        )}
                    </motion.div>
                )
            })}
        </AnimatePresence>
    )
}

type TreeChild = Record<string, any> | RetrieveProductTemplateAlgolia[]

export const RenderTree = ({
    tree,
    currentLevel = 0,
}: {
    tree: TreeChild
    currentLevel?: number
}) => {
    return (
        <>
            {Object.keys(tree).map((key) => {
                const child = tree[key as keyof typeof tree]
                return (
                    <Box key={key} className="pl-0.5 sm:pl-1">
                        {key !== 'products' && (
                            <Flex className="text-sky-600 gap-x-1 sm:gap-x-2">
                                <Text className="text-sm font-semibold">
                                    {_.capitalize(key.replace('-', ' '))}
                                </Text>
                            </Flex>
                        )}

                        <Space className="my-2 sm:my-1" />
                        {Array.isArray(child) && (
                            <Grid className="grid-cols-1 gap-y-6 sm:gap-y-4 lg:gap-y-3">
                                {child.map(
                                    (
                                        product: Partial<ProductTemplate> & {
                                            _highlightResult?: {
                                                title: {
                                                    value: string
                                                }
                                            }
                                        },
                                    ) => {
                                        return (
                                            <SearchHit
                                                key={product.id}
                                                {...product}
                                            />
                                        )
                                    },
                                )}
                            </Grid>
                        )}
                        {!Array.isArray(child) && (
                            <Box className="pl-2 sm:pl-6 lg:pl-8">
                                <RenderTree
                                    tree={child as TreeChild}
                                    currentLevel={currentLevel + 1}
                                />
                            </Box>
                        )}
                    </Box>
                )
            })}
        </>
    )
}

type ResultsCounterProps = {
    count: number
    searchQuery: string
}
export const ResultsCounter = ({ count, searchQuery }: ResultsCounterProps) => {
    return (
        <Box
            data-testid="search-bar-count"
            className="mx-auto text-xs text-neutral-600"
        >
            {!!count && (
                <Text component="span">
                    <b>{count}</b> {pluralWord('résultat', count)}
                </Text>
            )}

            {!count && !!searchQuery && (
                <Text
                    component="span"
                    className="text-xs lg:text-sm text-neutral-600"
                >
                    Aucun résultat pour le terme : <b>{searchQuery}</b>
                </Text>
            )}
        </Box>
    )
}
